/**
 * Formats the fields of a line's applied cut (its `Processing`) for the "Cut To" box: the text
 * shown for each cut field, and whether the cut leaves a field empty.
 *
 * Position:
 * - Callers: `components/Shared/DisplayDefineCut/DefineCutDisplay.tsx` (cart, lists, catalog,
 *   search, quotes, checkout, orders),
 *   `components/Product/ProductDisplayDefineCut/ProductDisplayDefineCut.tsx`
 *   (product page) and `components/Shared/ExcelExport/Products/ProductsExportUtils.tsx` (export).
 * - The field names are the keys of the objects that
 *   `components/Shared/DefineCutModal/DefineCutHelper.ts` → `getProductCutAttributes` returns for
 *   the product (`width`, `oneSide`, `insideTolerance`, ...). That file has the same name and holds
 *   the modal's rules; this one only formats a cut that already exists.
 *
 * Rules a change must keep:
 * - A cut's tolerance arrives under two sets of names: `toleranceMin` / `toleranceMax` /
 *   `toleranceMinInsideDiameter` / `toleranceMaxInsideDiameter` (the `Processing` of carts, lists
 *   and pricing) and `minTolerance` / `maxTolerance` / `minToleranceInnerDiameter` /
 *   `maxToleranceInnerDiameter` (quote lines: `components/Models/CreateQuoteRequest.model.tsx` →
 *   `QuoteProcessing`). Both are read, and the name read last wins when both are present. For the
 *   outer bounds that is `toleranceMin` / `toleranceMax`; for the inside-diameter bounds it is the
 *   quote names `minToleranceInnerDiameter` / `maxToleranceInnerDiameter`.
 * - A tolerance is shown as the `displayValue` of the product's `tolerances` option whose min and
 *   max equal the cut's, both compared as `toFixed(5)` strings. A cut whose tolerance is not among
 *   the product's options shows an empty value.
 *   `components/Shared/ProcessingDescription/ProcessingDescription.tsx`
 *   → `getTolerances` matches the same way; change the two together.
 * - Dimensions are inches and are shown with `inch.display` from `@ryerson/common.uom`.
 * - 0, `"0"`, `""`, `null` and `undefined` all count as missing (see `isFalsy`), so a zero
 *   dimension or a +/-0 tolerance is left out unless the caller adds it back, as `DefineCutDisplay`
 *   does for tolerances.
 */
import { ProcessingExtended } from "./../ProcessingDescription";
import { inch } from "@ryerson/common.uom";
import {
	DraftQuoteLineItem,
	LanguageToLocale,
	MatchingProduct,
	Processing,
} from "@ryerson/frontend.common";
import { ProcessingAttributesData, Product } from "@ryerson/frontend.common";
import { ProductItemData } from "../ProductDisplay/Helpers/ProductDisplay.models";
import { OrderDetailsCell } from "@components/OrderDetails/Helpers/OrderProductStepperComponents/OrderDetailsTableConfig";
import { CutProcessing, LineItem } from "@components/Models/QuoteDetails.models";
import { UpdatedOrderConfirmationCartItem } from "@components/Checkout/OrderConfirmation/OrderConfirmationDetailsTable/OrderConfirmationDetailsTableConfig";

/** Which of a cut's tolerance bounds {@link getProcessingTolerance} reads. */
enum tolerance {
	max = "max",
	min = "min",
	minInside = "minInside",
	maxInside = "maxInside",
}
const processingHasProp = (property: string, processing: ProcessingExtended) =>
	Object.prototype.hasOwnProperty.call(processing, property);

/** A number as a five-decimal string, the form tolerances are compared in. */
const numericStr = (val: Number) => Number(val)?.toFixed(5)?.toString();

/**
 * One tolerance bound of a cut as a five-decimal string, read from whichever of the two field
 * names the cut carries (see the file header). Returns `""` when the cut has neither name. A name
 * that is present with a `null` value reads as `"0.00000"`.
 */
const getProcessingTolerance = (toleranceType: tolerance, processing: ProcessingExtended) => {
	let val = "";
	switch (toleranceType) {
		case tolerance.max:
			if (processingHasProp("maxTolerance", processing)) {
				val = numericStr(processing?.maxTolerance || 0);
			}
			if (processingHasProp("toleranceMax", processing)) {
				val = numericStr(processing?.toleranceMax || 0);
			}
			return val;
		case tolerance.min:
			if (processingHasProp("minTolerance", processing)) {
				val = numericStr(processing?.minTolerance || 0);
			}
			if (processingHasProp("toleranceMin", processing)) {
				val = numericStr(processing?.toleranceMin || 0);
			}
			return val;

		case tolerance.minInside:
			if (processingHasProp("toleranceMinInsideDiameter", processing)) {
				val = numericStr(processing?.toleranceMinInsideDiameter || 0);
			}

			if (processingHasProp("minToleranceInnerDiameter", processing)) {
				val = numericStr(processing?.minToleranceInnerDiameter || 0);
			}
			return val;

		case tolerance.maxInside:
			if (processingHasProp("toleranceMaxInsideDiameter", processing)) {
				val = numericStr(processing?.toleranceMaxInsideDiameter || 0);
			}
			if (processingHasProp("maxToleranceInnerDiameter", processing)) {
				val = numericStr(processing?.maxToleranceInnerDiameter || 0);
			}
			return val;
		default:
			return val;
	}
};

/**
 * The text shown for one cut field.
 *
 * - `processing`, `finish`, `cutShape`: the `description` of the option in `processingData` (the
 *   product's `processingAttribute` for the user's locale) whose `code` equals the cut's. For
 *   `processing`, the cut's own `processTranslation[language]` is used instead when `language`
 *   is passed and the cut has one (quote, Easy Quote, checkout and order rows).
 * - `shortSide` shows `width`, `longSide` shows `length`, `oneSide` shows `width`. The define-cut
 *   modal fills both `width` and `length` from a square's one side.
 * - The tolerance fields show the matching option's `displayValue` from `product.tolerances`.
 * - Any other field shows `processing[field]` followed by the inch unit.
 *
 * Returns `""` when no option matches. Call it only for fields that
 * {@link isMissingProcessAttribute} reports present: for an absent dimension the dimension branches
 * produce `"undefined in"` or `"null in"`.
 */
export const displayAttributeValue = (
	field: string,
	processingData: ProcessingAttributesData,
	processing: ProcessingExtended & CutProcessing,
	product:
		| Product
		| MatchingProduct
		| ProductItemData
		| OrderDetailsCell["rowData"]
		| DraftQuoteLineItem
		| LineItem
		| UpdatedOrderConfirmationCartItem,
	language?: LanguageToLocale
) => {
	let chosenOpt: any = {};

	switch (field) {
		case "processing":
			if (processing.processTranslation && language) {
				return processing.processTranslation[language];
			} else {
				chosenOpt =
					processingData?.processes?.find(
						(option) => option.code === processing.process
					) || {};
				return chosenOpt?.description || "";
			}
		case "finish":
			chosenOpt =
				(processing.finish !== "" &&
					processingData?.finishes?.find(
						(option) => option.code === processing.finish
					)) ||
				{};
			return chosenOpt?.description || "";
		case "cutShape":
			chosenOpt =
				processingData?.cutShapes?.find((option) => option.code === processing.shape) || {};
			return chosenOpt?.description || "";
		case "shortSide":
			return `${processing?.width} ${inch.display}`;
		case "longSide":
			return `${processing?.length} ${inch.display}`;
		case "oneSide":
			// A template string is never empty, so this always returns the width; the length
			// alternative is never reached.
			return (
				`${processing?.width} ${inch.display}` || `${processing?.length} ${inch.display}`
			);
		case "tolerance":
		case "outsideTolerance":
		case "oneSideTolerance":
		case "widthLengthTolerance":
			chosenOpt =
				product?.tolerances?.find(({ toleranceMin, toleranceMax }: any) => {
					toleranceMin = numericStr(toleranceMin);
					toleranceMax = numericStr(toleranceMax);
					let processingTolMin = getProcessingTolerance(tolerance.min, processing);
					let procTolMax = getProcessingTolerance(tolerance.max, processing);
					return toleranceMin === processingTolMin && toleranceMax === procTolMax;
				}) || {};
			return chosenOpt?.displayValue || "";
		case "insideTolerance":
			chosenOpt =
				product?.tolerances?.find(({ toleranceMin, toleranceMax }: any) => {
					toleranceMin = numericStr(toleranceMin);
					toleranceMax = numericStr(toleranceMax);
					let processingMinInside = getProcessingTolerance(
						tolerance.minInside,
						processing
					);
					let processingMaxInside = getProcessingTolerance(
						tolerance.maxInside,
						processing
					);
					return (
						toleranceMin === processingMinInside && toleranceMax === processingMaxInside
					);
				}) || {};
			return chosenOpt?.displayValue || "";
		default:
			return `${processing[field as keyof Processing]} ${inch.display}` || "";
	}
};
/**
 * Whether a cut value counts as missing. Cut values arrive as numbers or as strings depending on
 * the API, so a numeric string is judged by its number: `"0"` and `"0.000"` are missing, like `0`.
 * A non-numeric string is missing only when empty. `undefined`, `null` and any object are missing.
 */
const isFalsy = (dataValue: any) => {
	const type = typeof dataValue;
	switch (true) {
		case type === "string":
			return isNaN(dataValue) ? !dataValue : !parseFloat(dataValue);
		case type === "boolean":
		case type === "number":
			return !dataValue;
		case type === "undefined":
		case dataValue === null:
		default:
			return true;
	}
};

/**
 * Whether the cut leaves the cut field `attribute` empty, by {@link isFalsy}. `shortSide` reads
 * `width` and `longSide` reads `length`. `oneSide` and the tolerance fields are missing only when
 * both of their values are. Displays drop a missing field from the "Cut To" box.
 */
export const isMissingProcessAttribute = (attribute: string, processing: ProcessingExtended) => {
	switch (attribute) {
		case "processing":
			return isFalsy(processing.process);
		case "finish":
			return isFalsy(processing.finish);
		case "cutShape":
			return isFalsy(processing.shape);
		case "shortSide":
			return isFalsy(processing.width);
		case "longSide":
			return isFalsy(processing.length);
		case "oneSide":
			return isFalsy(processing.length) && isFalsy(processing.width);
		case "tolerance":
		case "outsideTolerance":
		case "oneSideTolerance":
		case "widthLengthTolerance":
			return (
				isFalsy(getProcessingTolerance(tolerance.min, processing)) &&
				isFalsy(getProcessingTolerance(tolerance.max, processing))
			);
		case "insideTolerance":
			return (
				isFalsy(getProcessingTolerance(tolerance.minInside, processing)) &&
				isFalsy(getProcessingTolerance(tolerance.maxInside, processing))
			);

		case "width":
		case "length":
		default:
			return isFalsy(processing[attribute as keyof Processing]);
	}
};
